import React, { useState } from 'react';
import { Button, Modal, InputNumber, message } from 'antd';
import { FireOutlined } from '@ant-design/icons';
import { updateAdvanceBookingDays } from './advance-booking-days.module';

const WEEKDAYS = [
    { key: 'sunday', label: 'Sunday' },
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
];

const FARE_REGULAR = 'REGULAR';
const FARE_PEAK = 'PEAK';

const presetStyle = {
    background: '#ffffff',
    border: '1px solid #d8dce0',
    borderRadius: 9999,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: '#6b7280',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
};

const segmentGroupStyle = {
    display: 'inline-flex',
    background: '#eceef1',
    borderRadius: 12,
    padding: 4,
    gap: 4,
};

const segmentBaseStyle = {
    border: 'none',
    background: 'transparent',
    borderRadius: 9,
    padding: '8px 20px',
    minWidth: 104,
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    gap: 6,
};

const segmentIdleStyle = { ...segmentBaseStyle, color: '#6b7280' };

const segmentSelectedRegularStyle = {
    ...segmentBaseStyle,
    background: '#ffffff',
    color: '#1f2937',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
};

const segmentSelectedPeakStyle = {
    ...segmentBaseStyle,
    background: '#d6a417',
    color: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
};

// Every day defaults to Regular. Peak only applies when the operator sets it.
const buildDefaultFareMap = () => {
    const map = {};
    WEEKDAYS.forEach(({ key }) => {
        map[key] = FARE_REGULAR;
    });
    return map;
};

// Saved value may arrive as a parsed object or a JSON string from the API.
const normalizeFareMap = (saved) => {
    let parsed = saved;
    if (typeof saved === 'string') {
        try {
            parsed = JSON.parse(saved);
        } catch {
            parsed = null;
        }
    }
    if (!parsed || typeof parsed !== 'object') {
        return buildDefaultFareMap();
    }
    const map = buildDefaultFareMap();
    WEEKDAYS.forEach(({ key }) => {
        const value = parsed[key];
        if (value === FARE_REGULAR || value === FARE_PEAK) {
            map[key] = value;
        }
    });
    return map;
};

export const AdvanceBookingDays = ({ scheduleId, onSuccess, advanceBookingDays, defaultFareByWeekday }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [days, setDays] = useState(advanceBookingDays);
    const [fareMap, setFareMap] = useState(() => normalizeFareMap(defaultFareByWeekday));
    const [loading, setLoading] = useState(false);

    const openModal = () => {
        setDays(advanceBookingDays);
        setFareMap(normalizeFareMap(defaultFareByWeekday));
        setIsModalVisible(true);
    };

    const setDayFare = (key, fareType) => {
        setFareMap((prev) => ({ ...prev, [key]: fareType }));
    };

    const applyPreset = (resolver) => {
        const next = {};
        WEEKDAYS.forEach(({ key }) => {
            next[key] = resolver(key);
        });
        setFareMap(next);
    };

    const handleOk = async () => {
        try {
            setLoading(true);
            await updateAdvanceBookingDays(scheduleId, days, fareMap);
            message.success('Advance booking days updated successfully');
            setIsModalVisible(false);
            onSuccess?.();
        } catch (error) {
            message.error('Failed to update advance booking days');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button onClick={openModal}>
                Advance Booking Days
            </Button>
            <Modal
                title="Set Advance Booking Days"
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={loading}
                width={580}
            >
                <InputNumber
                    min={0}
                    max={365}
                    value={days}
                    onChange={(value) => setDays(value)}
                    placeholder="Enter number of days"
                    style={{ width: '100%' }}
                />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 20, marginBottom: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>Default fare by day of week</span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" style={presetStyle} onClick={() => applyPreset((key) => (key === 'sunday' || key === 'saturday' ? FARE_PEAK : FARE_REGULAR))}>
                            Weekends Peak
                        </button>
                        <button type="button" style={presetStyle} onClick={() => applyPreset(() => FARE_REGULAR)}>
                            All Regular
                        </button>
                        <button type="button" style={presetStyle} onClick={() => applyPreset(() => FARE_PEAK)}>
                            All Peak
                        </button>
                    </div>
                </div>

                <div style={{ border: '1px solid #ebedf0', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f7f8fa', borderBottom: '1px solid #ebedf0' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, color: '#9ca3af' }}>DAY</span>
                        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, color: '#9ca3af' }}>DEFAULT FARE</span>
                    </div>
                    {WEEKDAYS.map(({ key, label }, i) => {
                        const isPeak = fareMap[key] === FARE_PEAK;
                        return (
                            <div
                                key={key}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 16px',
                                    borderBottom: i === WEEKDAYS.length - 1 ? 'none' : '1px solid #f0f1f3',
                                }}
                            >
                                <span style={{ fontSize: 15, color: '#1f2937' }}>{label}</span>
                                <div style={segmentGroupStyle}>
                                    <button
                                        type="button"
                                        onClick={() => setDayFare(key, FARE_REGULAR)}
                                        style={!isPeak ? segmentSelectedRegularStyle : segmentIdleStyle}
                                    >
                                        Regular
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDayFare(key, FARE_PEAK)}
                                        style={isPeak ? segmentSelectedPeakStyle : segmentIdleStyle}
                                    >
                                        <FireOutlined />Peak
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 14, lineHeight: 1.5 }}>
                    When the schedule opens, each future date is stamped with its weekday's default fare.
                    You can still override individual dates on the calendar afterward.
                </p>
            </Modal>
        </>
    );
};
